import Page from 'flarum/common/components/Page';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import app from 'flarum/forum/app';

export default class WwaLandingPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle('Wealth Warrior Awakenings 101');
    app.setTitleCount(0);
  }

  view() {
    const isLoggedIn = !!(app.session && app.session.user);

    return (
      <div className="WwaLandingPage">
        {/* Hero */}
        <section className="Wwa-hero">
          <div className="Wwa-hero-backdrop" aria-hidden="true">
            <div className="Wwa-hero-sun" />
            <div className="Wwa-hero-kanji">武士道</div>
          </div>
          <div className="Wwa-hero-overlay" aria-hidden="true" />
          <div className="Wwa-hero-content">
            <div className="container">
              <div className="Wwa-hero-text">
                <div className="Wwa-badge">A 90-Day Discipline · 9 Awakenings</div>
                <h1 className="Wwa-title">
                  Wealth Warrior
                  <span className="Wwa-title-accent">Awakenings 101</span>
                </h1>
                <p className="Wwa-tagline">
                  Honor. Integrity. Mastery. A daily practice in becoming what you already know you should be.
                </p>
                {!isLoggedIn && (
                  <button
                    className="Button Wwa-cta"
                    onclick={() => this.handleSignUp()}
                  >
                    Begin the Path
                  </button>
                )}
                {isLoggedIn && (
                  <a
                    href={app.route('courses')}
                    className="Button Wwa-cta"
                  >
                    Enter the Dojo
                  </a>
                )}
                <div className="Wwa-hero-note">Free enrollment. Daily integrations. No spectators.</div>
              </div>
            </div>
          </div>
        </section>

        {/* Creed */}
        <section className="Wwa-creed">
          <div className="container">
            <div className="Wwa-creed-frame">
              <blockquote className="Wwa-creed-quote">
                A master knows he has not "learned" anything until what he has learned
                has changed who he is, what he does, or how he does.
              </blockquote>
              <div className="Wwa-creed-attr">— Michael Ivan Skye</div>
            </div>
            <div className="Wwa-creed-body">
              <p>
                Reading is not learning. Knowing is not doing. Most men walk through their lives
                agreeing and disagreeing with ideas, filing insights they never use. The Wealth Warrior
                takes a different path.
              </p>
              <p>
                This course is a <strong>discipline of integration</strong> — the daily practice of
                putting what you read into words, your words into actions, and your actions into
                upgrades to the system you call your life.
              </p>
            </div>
          </div>
        </section>

        {/* Awakenings */}
        <section className="Wwa-awakenings">
          <div className="container">
            <header className="Wwa-section-head">
              <div className="Wwa-section-kicker">The Curriculum</div>
              <h2 className="Wwa-section-title">Nine Awakenings</h2>
              <p className="Wwa-section-subtitle">
                One session at a time. Read, integrate in writing, post completed actions.
                Advance when the integration is real — not when the reading is done.
              </p>
            </header>

            <ol className="Wwa-awakenings-list">
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">01</span>
                <div>
                  <h3>Welcome &amp; Orientation</h3>
                  <p>The 90-day contract with yourself. What integration is, why past-tense actions matter, and what mastery actually looks like.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">02</span>
                <div>
                  <h3>Know · Be Clear · Be Awake</h3>
                  <p>Three states. Most men stop at the first. The warrior designs a life that keeps him in the third.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">03</span>
                <div>
                  <h3>Wealth vs. Riches</h3>
                  <p>An abundance of values, not an abundance of things. Choose what is of value to you — then build it, aggressively.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">04</span>
                <div>
                  <h3>Awakenings vs. Awake</h3>
                  <p>The seduction of feeling chosen. The discipline of staying awake. A test that cuts through belief systems.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">05</span>
                <div>
                  <h3>Businessman vs. Wealth Warrior</h3>
                  <p>The businessman pursues profit. The Wealth Warrior makes his <em>life</em> his business — and builds systems to deliver.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">06</span>
                <div>
                  <h3>Words · Actions · Upgrades</h3>
                  <p>Saying is not acting. Acting is not lasting. The warrior upgrades the system so the action repeats itself.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">07</span>
                <div>
                  <h3>Insights · Notes · Upgrades</h3>
                  <p>Insights vanish. Notes rot in drawers. Upgrades become who you are. Learn the difference.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">08</span>
                <div>
                  <h3>Change vs. System Upgrade</h3>
                  <p>Why "just be yourself" is a trap. The systems mentality that turns challenge into fuel.</p>
                </div>
              </li>
              <li className="Wwa-awakening">
                <span className="Wwa-awakening-num">09</span>
                <div>
                  <h3>The Honest Review</h3>
                  <p>Look back. Measure the man. What changed? What held? What's next?</p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        {/* Discipline */}
        <section className="Wwa-discipline">
          <div className="container">
            <header className="Wwa-section-head">
              <div className="Wwa-section-kicker">The Practice</div>
              <h2 className="Wwa-section-title">How the Dojo Works</h2>
            </header>

            <div className="Wwa-discipline-grid">
              <article className="Wwa-tile">
                <div className="Wwa-tile-num">一</div>
                <h3>One Module Per Day</h3>
                <p>Read the awakening. Verbalize your integration in writing. Post. The next module opens at 8:00 AM Eastern the following day.</p>
              </article>
              <article className="Wwa-tile">
                <div className="Wwa-tile-num">二</div>
                <h3>Integrate, Don't Summarize</h3>
                <p>Teach the class back to yourself. Connect thoughts. Build clarity. Minimum 500 characters of real integration to advance.</p>
              </article>
              <article className="Wwa-tile">
                <div className="Wwa-tile-num">三</div>
                <h3>Past-Tense Actions Only</h3>
                <p>Not "I will." Not "I plan to." What did you do as a result? What did you schedule, upgrade, implement?</p>
              </article>
              <article className="Wwa-tile">
                <div className="Wwa-tile-num">四</div>
                <h3>Record Every Posting</h3>
                <p>Your integrations and actions are emailed back to you with the link to the next session. A trail of becoming.</p>
              </article>
            </div>
          </div>
        </section>

        {/* Path filter */}
        <section className="Wwa-path">
          <div className="container">
            <header className="Wwa-section-head">
              <div className="Wwa-section-kicker">The Standard</div>
              <h2 className="Wwa-section-title">Walk This Path If…</h2>
            </header>

            <ul className="Wwa-path-list">
              <li>You are done with the shelf of half-read books and the folder of "someday" notes.</li>
              <li>You want a system for becoming, not another inspirational rush.</li>
              <li>You are willing to write the integration no one else will see if you half-ass it.</li>
              <li>You believe mastery is a daily practice, not a personality trait.</li>
              <li>You would rather be tested than flattered.</li>
            </ul>
          </div>
        </section>

        {/* Final CTA */}
        <section className="Wwa-final">
          <div className="container">
            <div className="Wwa-final-inner">
              <h2 className="Wwa-final-title">The Path Is Narrow. Walk It.</h2>
              <p className="Wwa-final-text">
                Ninety days. Nine awakenings. One version of you on the other side —
                the one you already know you should be.
              </p>
              {!isLoggedIn && (
                <button
                  className="Button Wwa-cta Wwa-cta--large"
                  onclick={() => this.handleSignUp()}
                >
                  Begin the Path
                </button>
              )}
              {isLoggedIn && (
                <a
                  href={app.route('courses')}
                  className="Button Wwa-cta Wwa-cta--large"
                >
                  Enter the Dojo
                </a>
              )}
              <div className="Wwa-final-note">武士道 · WEALTH WARRIOR</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  handleSignUp() {
    app.modal.show(SignUpModal, {
      courseEnrollmentKey: 'course-wwa-101',
    });
  }
}
